/* import __COLOCATED_TEMPLATE__ from './suggestion-sample-api-item.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'embercom/services/intl';
import type NotificationsService from 'embercom/services/notifications-service';
import copyToClipboard from 'copy-to-clipboard';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import { type PulseAccordionSection } from 'glint/pulse';

export interface SampleApiDoc {
  requestMethod: string;
  requestUrl: string;
  requestBody: Record<string, any>;
  responseBody: Record<string, any>;
}

interface Signature {
  Args: {
    section: PulseAccordionSection;
    sectionId: string;
    isOpen: boolean;
    sampleApiDoc: SampleApiDoc;
  };
}

export default class SuggestionSampleApiItem extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @tracked isHoveringSampleRequest = false;
  @tracked isHoveringCopyButton = false;
  @tracked isHoveringSampleResponse = false;
  @tracked isHoveringCopyResponseButton = false;

  get formattedApiDocumentationDescription() {
    let text = this.intl.t(
      'automation.analyze.task-suggestions.api-docs-side-drawer.api_documentation.description',
    );
    return sanitizeHtml(text);
  }

  get formattedActionParams() {
    if (!this.hasActionParams) {
      return '{}';
    }
    return JSON.stringify(this.args.sampleApiDoc.requestBody, null, 2);
  }

  get hasActionParams() {
    return Object.keys(this.args.sampleApiDoc.requestBody).length > 0;
  }

  get hasMockResponse() {
    return Object.keys(this.args.sampleApiDoc.responseBody).length > 0;
  }

  get formattedMockResponse() {
    if (!this.hasMockResponse) {
      return '{}';
    }
    return JSON.stringify(this.args.sampleApiDoc.responseBody, null, 2);
  }

  @action
  copyToClipboardWithNotification(text: string, successKey: string, errorKey: string) {
    try {
      let success = copyToClipboard(text);

      if (success) {
        this.notificationsService.notifyConfirmation(this.intl.t(successKey));
      } else {
        throw new Error('Copy to clipboard failed');
      }
    } catch (error) {
      this.notificationsService.notifyError(this.intl.t(errorKey));
    }
  }

  @action
  onCopyActionParams() {
    this.copyToClipboardWithNotification(
      this.formattedActionParams,
      'automation.analyze.task-suggestions.api-docs-side-drawer.data_inputs.copied',
      'automation.analyze.task-suggestions.api-docs-side-drawer.data_inputs.copy_failed',
    );
  }

  @action
  onHoverSampleRequest() {
    this.isHoveringSampleRequest = true;
  }

  @action
  onLeaveSampleRequest() {
    this.isHoveringSampleRequest = false;
  }

  @action
  onCopyButtonHover() {
    this.isHoveringCopyButton = true;
  }

  @action
  onCopyButtonLeave() {
    this.isHoveringCopyButton = false;
  }

  @action
  onCopyMockResponse() {
    this.copyToClipboardWithNotification(
      this.formattedMockResponse,
      'automation.analyze.task-suggestions.api-docs-side-drawer.example_response.copied',
      'automation.analyze.task-suggestions.api-docs-side-drawer.example_response.copy_failed',
    );
  }

  @action
  onHoverSampleResponse() {
    this.isHoveringSampleResponse = true;
  }

  @action
  onLeaveSampleResponse() {
    this.isHoveringSampleResponse = false;
  }

  @action
  onCopyResponseButtonHover() {
    this.isHoveringCopyResponseButton = true;
  }

  @action
  onCopyResponseButtonLeave() {
    this.isHoveringCopyResponseButton = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::SuggestionAccordionItems::SuggestionSampleApiItem': typeof SuggestionSampleApiItem;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/suggestion-accordion-items/suggestion-sample-api-item': typeof SuggestionSampleApiItem;
  }
}
